import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Download, Eye, FileText, RefreshCw, Search, CheckCircle2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import API, { SellOutAPI } from '../../services/api';
import { addTab } from '../../tabs/tabsSlice';
import {
  commitmentStatusLabel,
  downloadCsv,
  productLabel,
  statusOptions,
  supplierLabel,
} from '../../components/sellout/selloutUtils';

const FOLLOW_UP_STORAGE_KEY = 'sellout_missing_follow_up';

const missingHeaders = [
  { key: 'supplier', label: 'Proveedor', value: (row) => row?.supplier?.name || '' },
  { key: 'commitment', label: 'Promesa', value: (row) => row?.commitment?.reference || '' },
  { key: 'date', label: 'Fecha', value: (row) => row?.commitment?.date || '' },
  { key: 'line', label: 'Linea', value: (row) => row?.line?.line_number || row?.line?.id || '' },
  { key: 'expected_product', label: 'Producto esperado', value: (row) => productLabel(row?.expected_product) || '' },
  { key: 'expected_qty', label: 'Esperado' },
  { key: 'received_qty', label: 'Recibido' },
  { key: 'missing_qty', label: 'Faltante' },
  { key: 'fiscal_documents', label: 'Documentos fiscales', value: (row) => (row?.fiscal_documents || []).map((doc) => doc.reference).filter(Boolean).join(' | ') },
  { key: 'status', label: 'Estado' },
  { key: 'suggested_action', label: 'Accion sugerida' },
];

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

function readFollowUp() {
  try {
    const raw = localStorage.getItem(FOLLOW_UP_STORAGE_KEY);
    const values = raw ? JSON.parse(raw) : [];
    return Array.isArray(values) ? values.reduce((acc, id) => ({ ...acc, [id]: true }), {}) : {};
  } catch {
    return {};
  }
}

function writeFollowUp(values) {
  try {
    localStorage.setItem(FOLLOW_UP_STORAGE_KEY, JSON.stringify(Object.keys(values).filter((key) => values[key])));
  } catch {}
}

export default function SellOutMissing() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followUp, setFollowUp] = useState(readFollowUp);
  const [filters, setFilters] = useState({
    supplier_id: null,
    date_from: '',
    date_to: '',
    status: '',
    search: '',
  });
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });

  useEffect(() => {
    dispatch(addTab({ label: 'Faltantes Sell Out', path: '/sellout/missing' }));
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    API.get('suppliers/minimal/', { params: { active: true } })
      .then(({ data }) => {
        if (mounted) setSuppliers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (mounted) setSnack({ open: true, severity: 'error', message: 'No se pudo cargar proveedores.' });
      });
    return () => {
      mounted = false;
    };
  }, []);

  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => supplier.id === filters.supplier_id) || null,
    [filters.supplier_id, suppliers]
  );

  const loadMissing = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.status) params.status = filters.status;
      if (filters.search.trim()) params.search = filters.search.trim();
      const { data } = await SellOutAPI.missing(params);
      setRows(asArray(data));
    } catch {
      setRows([]);
      setSnack({ open: true, severity: 'error', message: 'No se pudo cargar el reporte de faltantes.' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadMissing();
  }, [loadMissing]);

  const groupedRows = useMemo(() => {
    const grouped = {};
    rows.forEach((row) => {
      const key = row?.supplier?.id || 'sin-proveedor';
      if (!grouped[key]) {
        grouped[key] = {
          supplier: row?.supplier || { id: key, name: 'Sin proveedor' },
          rows: [],
          missing: 0,
        };
      }
      grouped[key].rows.push(row);
      grouped[key].missing += Number(row?.missing_qty || 0);
    });
    return Object.values(grouped).sort((a, b) => (a.supplier?.name || '').localeCompare(b.supplier?.name || ''));
  }, [rows]);

  const toggleFollowUp = (row) => {
    setFollowUp((prev) => {
      const next = { ...prev, [row.id]: !prev[row.id] };
      writeFollowUp(next);
      return next;
    });
    setSnack({ open: true, severity: 'success', message: 'Seguimiento marcado en esta pantalla.' });
  };

  const exportRows = (rowsToExport = rows, filename = 'sellout-faltantes.csv') => {
    downloadCsv(filename, missingHeaders, rowsToExport);
  };

  const makeColumns = () => [
    {
      field: 'commitment',
      headerName: 'Promesa',
      minWidth: 150,
      flex: 0.8,
      valueGetter: (value) => value?.reference || '',
    },
    {
      field: 'line',
      headerName: 'Linea',
      width: 90,
      valueGetter: (value) => value?.line_number || value?.id || '',
    },
    {
      field: 'expected_product',
      headerName: 'Producto esperado',
      minWidth: 240,
      flex: 1,
      valueGetter: (value) => productLabel(value) || '-',
    },
    { field: 'expected_qty', headerName: 'Esperado', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'received_qty', headerName: 'Recibido', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'missing_qty', headerName: 'Faltante', width: 110, align: 'right', headerAlign: 'right' },
    {
      field: 'fiscal_documents',
      headerName: 'Fiscal',
      width: 180,
      valueGetter: (value) => (value || []).map((doc) => doc.reference).filter(Boolean).join(', ') || '-',
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 170,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip size="small" label={params.value === 'requires_review' ? 'Revision' : params.value === 'pending_credit_note' ? 'Pendiente NC' : 'Abierto'} color={params.value === 'requires_review' ? 'warning' : 'default'} />
          {followUp[params.row.id] ? <Chip size="small" label="Seguimiento" color="info" /> : null}
        </Stack>
      ),
    },
    { field: 'suggested_action', headerName: 'Accion sugerida', minWidth: 180, flex: 0.8 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 170,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Ver promesa">
            <IconButton size="small" onClick={() => navigate(`/sellout/promotions/${params.row.commitment?.id}`)}>
              <Eye size={17} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Registrar NC">
            <IconButton size="small" onClick={() => navigate('/sellout/documents/create', {
              state: {
                selloutDocumentDefaults: {
                  supplier_id: params.row.supplier?.id || null,
                  commitment_id: params.row.commitment?.id || null,
                  document_type: 'credit_note',
                },
              },
            })}>
              <FileText size={17} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Marcar seguimiento">
            <IconButton size="small" onClick={() => toggleFollowUp(params.row)}>
              <CheckCircle2 size={17} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const totals = useMemo(() => rows.reduce((acc, row) => ({
    expected: acc.expected + Number(row.expected_qty || 0),
    received: acc.received + Number(row.received_qty || 0),
    missing: acc.missing + Number(row.missing_qty || 0),
    review: acc.review + (row.status === 'requires_review' ? 1 : 0),
  }), { expected: 0, received: 0, missing: 0, review: 0 }), [rows]);

  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto', width: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Faltantes Sell Out</Typography>
          <Typography variant="body2" color="text.secondary">
            Faltante {totals.missing} / Recibido {totals.received} / Revision {totals.review}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshCw size={17} />} onClick={loadMissing} disabled={loading}>
            Actualizar
          </Button>
          <Button variant="outlined" startIcon={<Download size={17} />} onClick={() => exportRows()} disabled={!rows.length}>
            Exportar reporte
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 1, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
          <Autocomplete
            options={suppliers}
            value={selectedSupplier}
            onChange={(_, supplier) => setFilters((prev) => ({ ...prev, supplier_id: supplier?.id || null }))}
            getOptionLabel={supplierLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => <TextField {...params} size="small" label="Proveedor" />}
            sx={{ minWidth: { md: 260 }, flex: 1 }}
          />
          <TextField
            size="small"
            type="date"
            label="Desde"
            value={filters.date_from}
            onChange={(event) => setFilters((prev) => ({ ...prev, date_from: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { md: 150 } }}
          />
          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={filters.date_to}
            onChange={(event) => setFilters((prev) => ({ ...prev, date_to: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { md: 150 } }}
          />
          <TextField
            select
            size="small"
            label="Estado promesa"
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            sx={{ minWidth: { md: 170 } }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label || commitmentStatusLabel(option.value)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Buscar"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            InputProps={{ startAdornment: <Search size={17} style={{ marginRight: 8 }} /> }}
            sx={{ minWidth: { md: 220 }, flex: 1 }}
          />
        </Stack>
      </Paper>

      {!loading && rows.length === 0 ? (
        <Alert severity="info">No hay faltantes abiertos con los filtros actuales.</Alert>
      ) : null}

      <Stack spacing={2}>
        {groupedRows.map((group) => (
          <Paper key={group.supplier?.id || group.supplier?.name} variant="outlined" sx={{ borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={800}>{group.supplier?.name || 'Sin proveedor'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.rows.length} linea{group.rows.length === 1 ? '' : 's'} / faltante {group.missing}
                </Typography>
              </Box>
              <Button variant="text" startIcon={<Download size={17} />} onClick={() => exportRows(group.rows, `sellout-faltantes-${group.supplier?.id || 'proveedor'}.csv`)}>
                Exportar proveedor
              </Button>
            </Stack>
            <Box sx={{ height: Math.min(520, 130 + group.rows.length * 58) }}>
              <DataGrid
                rows={group.rows}
                columns={makeColumns()}
                loading={loading}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={{ border: 0 }}
              />
            </Box>
          </Paper>
        ))}
      </Stack>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
